import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoaderSkeleton, WithErrorBoundary } from '@bloomays-lib/ui.shared';
import { notify } from '../../helpers/toastify';
import { errorLogger, getApolloErrorCode } from '../../helpers/error';
import { GET_DEAL_AND_JOB_FUNNEL_TALENTS } from '@bloomays-lib/adapter.api-talent';
import {
  ApolloGqlJobFunnelWithTalents,
  DealType,
  Role,
  StatusType,
  GqlJobFunnel,
  GqlTalent,
} from '@bloomays-lib/types.shared';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DataGrid from '../molecules/DataGrid';
import { GridColDef, GridRowClassNameParams } from '@mui/x-data-grid-premium';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { useAuthStore } from '../../stores';
import { format } from 'date-fns/fp/format';

interface JobFunnelDisplayProps {
  data: {
    getJobFunnelProjectWithTalents: ApolloGqlJobFunnelWithTalents;
  };
  isSystem: boolean;
}

const HiresweetLink = (args: { job: GqlJobFunnel }) => {
  const { job } = args;
  return (
    <HrefLink
      href={`${import.meta.env.VITE_HIRESWEET_FRONT_URL}/reveal/projects/recruiting/${job.hiresweetId}/profiles`}
      target="_blank"
    >
      Hiresweet
    </HrefLink>
  );
};

const PipefyLink = (args: { job: GqlJobFunnel; isSystem: boolean }) => {
  const { job, isSystem } = args;
  if (job.pipefyId && isSystem) {
    return (
      <HrefLink href={`https://app.pipefy.com/open-cards/${job.pipefyId}`} target="_blank">
        Pipefy
      </HrefLink>
    );
  }
  return <span>Pipefy</span>;
};

const HubspotLink = (args: { job: GqlJobFunnel }) => {
  const { job } = args;
  return (
    <HrefLink href={`https://app-eu1.hubspot.com/contacts/139539088/record/0-3/${job.hubspotDealId}`} target="_blank">
      Hubspot
    </HrefLink>
  );
};

const JobFunnelDisplay = (args: JobFunnelDisplayProps) => {
  const { t } = useTranslation(['jobs']);
  const { job, bloomer, talents = [] } = args.data.getJobFunnelProjectWithTalents;

  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <HrefLink
          href={`/talentsV2/${params.row.id}`}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <VisibilityIcon />
        </HrefLink>
      ),
    },
    {
      field: 'firstname',
      headerName: t('firstname'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      width: 200,
    },
    {
      field: 'lastname',
      headerName: t('lastname'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      width: 200,
    },
    {
      field: 'email',
      headerName: t('email'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      width: 200,
    },
    {
      field: 'jobTitle',
      headerName: t('jobTitle'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      width: 200,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Détails pour le job {job.name}
        {job.society?.name && ` x ${job.society.name}`}
      </Typography>

      <Container>
        <InfoDiv>
          <InfoItem>
            <Label>Nom:</Label>
            {job.name}
          </InfoItem>
          <InfoItem>
            <Label>Société:</Label>
            {job.society?.name}
          </InfoItem>
          <InfoItem>
            <Label>{t('jobFunnelDetailRecruiter')}:</Label>
            {job.recruiter?.email}
          </InfoItem>
          <InfoItem>
            <Label>{t('jobFunnelDetailJobOwner')}:</Label>
            {job.jobOwner?.email}
          </InfoItem>
          <InfoItem>
            <Label>Type de Job:</Label>
            {job.jobType}
          </InfoItem>
          <InfoItem>
            <Label>Type de Deal:</Label>
            {job.dealType}
          </InfoItem>
          <InfoItem>
            <Label>Durée:</Label>
            {job.duration} mois
          </InfoItem>
          <InfoItem>
            <Label>Localisation:</Label>
            {job.location}
          </InfoItem>
          <InfoItem>
            <Label>Jours sur site:</Label>
            {job.onSiteDaysPerWeek} jours/semaine
          </InfoItem>
          <InfoItem>
            <Label>Jours en remote:</Label>
            {job.remoteDaysPerWeek} jours/semaine
          </InfoItem>
        </InfoDiv>

        <InfoDiv>
          <InfoItem>
            <Label>Suivi du projet réalisé sur:</Label>
            <Box sx={{ display: 'inline-flex', gap: 2, ml: 1 }}>
              {job.hiresweetId && <HiresweetLink job={job} />}
              {job.pipefyId && <PipefyLink job={job} isSystem={args.isSystem} />}
              {job.hubspotDealId && <HubspotLink job={job} />}
            </Box>
          </InfoItem>
          <InfoItem>
            <Label>Date de création:</Label>
            {job.createdAt ? new Date(job.createdAt).toLocaleDateString('fr-FR') : 'Non spécifiée'}
          </InfoItem>
          <InfoItem>
            <Label>Lead Customer Status:</Label>
            {job.leadCustomerStatus}
          </InfoItem>
          <InfoItem>
            <Label>Lead Origin:</Label>
            {job.leadOrigin}
          </InfoItem>
          <InfoItem>
            <Label>Lead Canal:</Label>
            {job.leadCanal}
          </InfoItem>
          <InfoItem>
            <Label>Lead Canal Detail:</Label>
            {job.leadCanalDetail}
          </InfoItem>
        </InfoDiv>

        <InfoDiv>
          <InfoItem>
            <Label>Status:</Label>
            {job.status}
          </InfoItem>
          {bloomer && (
            <InfoItem>
              <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                <Label>Bloomer:</Label>
                <HrefLink href={`/talentsV2/${bloomer.id}`} target="_blank">
                  {bloomer.firstname} {bloomer.lastname}
                </HrefLink>
              </Box>
            </InfoItem>
          )}
          {job.status === StatusType.win && (
            <>
              {job.winDate && (
                <InfoItem>
                  <Label>Date de win:</Label>
                  {format('dd/MM/yyyy', new Date(job.winDate))}
                </InfoItem>
              )}
              {job.commercialMonth && (
                <InfoItem>
                  <Label>Mois commercial:</Label>
                  {format('MM/yyyy', new Date(job.commercialMonth))}
                </InfoItem>
              )}
              {job.buteur && (
                <InfoItem>
                  <Label>Recruteur:</Label>
                  {job.buteur}
                </InfoItem>
              )}
              {job.defenseur && (
                <InfoItem>
                  <Label>Sourceur:</Label>
                  {job.defenseur}
                </InfoItem>
              )}
              {job.passeur && (
                <InfoItem>
                  <Label>Apporteur:</Label>
                  {job.passeur}
                </InfoItem>
              )}
              {job.capitaine && (
                <InfoItem>
                  <Label>Job owner:</Label>
                  {job.capitaine}
                </InfoItem>
              )}
              {job.airtableId && job.dealType !== DealType.CDI_TECH && job.dealType !== DealType.CDI_PEOPLE && (
                <InfoItem>
                  <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                    <Label>Lien vers la page mission</Label>
                    <HrefLink href={`/missions/${job.airtableId}`} target="_blank">
                      <OpenInNewIcon />
                    </HrefLink>
                  </Box>
                </InfoItem>
              )}
            </>
          )}
          {job.status !== StatusType.win && (
            <>
              {job.lostReason && (
                <InfoItem>
                  <Label>Raison de perte:</Label>
                  {job.lostReason}
                </InfoItem>
              )}
              {job.otherLostReason && (
                <InfoItem>
                  <Label>Autre raison de perte:</Label>
                  {job.otherLostReason}
                </InfoItem>
              )}
            </>
          )}
        </InfoDiv>
      </Container>

      <Container>
        <InfoDiv>
          <InfoItem>
            <Label>ScoreCard 1:</Label>
            <Box sx={{ whiteSpace: 'pre-line' }}>{job.scoreCard1}</Box>
          </InfoItem>
          <InfoItem>
            <Label>ScoreCard 2:</Label>
            <Box sx={{ whiteSpace: 'pre-line' }}>{job.scoreCard2}</Box>
          </InfoItem>
          <InfoItem>
            <Label>ScoreCard 3:</Label>
            <Box sx={{ whiteSpace: 'pre-line' }}>{job.scoreCard3}</Box>
          </InfoItem>
          <InfoItem>
            <Label>ScoreCard 4:</Label>
            <Box sx={{ whiteSpace: 'pre-line' }}>{job.scoreCard4}</Box>
          </InfoItem>
        </InfoDiv>

        <ScrollableDiv>
          <InfoItem>
            <Label>Contexte:</Label>
            <Box sx={{ whiteSpace: 'pre-line' }}>{job.context}</Box>
          </InfoItem>
        </ScrollableDiv>
      </Container>

      <Container>
        <InfoDiv>
          <InfoItem>
            <Label>Détails:</Label>
            <Box sx={{ whiteSpace: 'pre-line' }}>{job.details}</Box>
          </InfoItem>
        </InfoDiv>
      </Container>
      <>
        <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
          Liste de Talents attachés au job
        </Typography>
        <Box sx={{ width: '90%', mx: 'auto' }}>
          <DataGrid
            uniqueDatagridId="job-funnel-talents"
            rows={talents}
            columns={columns}
            pageSize={10}
            hideFooterPagination={false}
            sortingMode="client"
            filterMode="client"
            getRowId={(row: GqlTalent) => row.id}
            getRowClassName={(params: GridRowClassNameParams) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            sx={{
              width: '100%',
            }}
          />
        </Box>
      </>
    </Box>
  );
};

const JobFunnelProject = () => {
  const auth = useAuthStore((state) => state.auth);
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;

  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['notify', 'random']);

  const { loading, error, data } = useQuery(GET_DEAL_AND_JOB_FUNNEL_TALENTS, {
    variables: {
      jobId: id,
    },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return <LoaderSkeleton label={t('loadingText', { ns: 'random' })} height={600} width={800} />;
  }

  if (error) {
    const errorCode = getApolloErrorCode(error);

    switch (errorCode) {
      case 'OBJECT_NOT_FOUND':
        notify('error', t('jobFunnelProjectNotFound'));
        break;
      default:
        errorLogger(error, {
          extraInfos: `Internal server error, impossible to retrieve data about job funnel project: ${id}`,
        });
        notify('error', t('randomLKError'), error);
    }
    return <div>Une erreur est survenue lors du chargement du projet</div>;
  }

  return <JobFunnelDisplay data={data} isSystem={isSystem} />;
};

const Container = styled(Box)(
  ({ theme }) => `
  display: flex;
  gap: 20px;
  padding: 0 20px;
  border-radius: 10px;
  width: 100%;
  & > * {
    flex: 1;
  }
`,
);

const InfoDiv = styled(Box)(
  ({ theme }) => `
  text-align: left;
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 4px;
  max-height: 350px;
  overflow-y: auto;
  background-color: ${theme.palette.neutral[10]};
`,
);

const ScrollableDiv = styled(Box)(
  ({ theme }) => `
  flex: 1;
  max-height: 350px;
  overflow-y: auto;
  text-align: left;
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 4px;
  background-color: ${theme.palette.neutral[10]};
`,
);

const InfoItem = styled(Box)(
  ({ theme }) => `
  margin-bottom: 10px;
  text-align: left;
  background-color: ${theme.palette.neutral[10]};
  border-radius: 10px;
`,
);

const Label = styled(Typography)(
  ({ theme }) => `
  font-weight: bold;
  margin-right: 8px;
  display: inline-block;
`,
);

export default WithErrorBoundary(JobFunnelProject);
